/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ShimmerText } from '../../compact-research-steps/ShimmerText';
import { EllipsesAnimation } from '../../primitives/ellipses-animation/EllipsesAnimation';
import { MorphingShapeLoader } from '../../primitives/morphing-shape-loader/MorphingShapeLoader';
import { LazyAnimatedRovoIcon } from '../../primitives/rovo-animated-icon/LazyAnimatedRovoIcon';

const styles = cssMap({
	root: {
		height: '24px',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'left',
		flexWrap: 'nowrap',
		alignItems: 'center',
	},
	// When bottomAdornment is present, the root height is auto and it grows to fill available space
	rootWithAdornment: {
		flex: '1',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'left',
		flexWrap: 'nowrap',
		alignItems: 'center',
		gap: token('space.100'),
	},
	iconContainer: {
		marginRight: token('space.025'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	// Column that holds the text row + bottomAdornment, growing to fill available space
	textColumn: {
		flex: '1',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	// Row within textColumn that holds the text + ellipses
	textRow: {
		height: '24px',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
	},
	text: {
		color: token('color.text'),
		font: token('font.body'),
		wordBreak: 'break-word',
	},
	textNoWrap: {
		color: token('color.text'),
		font: token('font.body'),
		whiteSpace: 'nowrap',
		overflow: 'hidden',
	},
	ellipsesContainer: {
		flex: 1,
	},
});

type RovoThinkingIndicatorProps = {
	// text shown between the Rovo icon and ellipses
	title: string;
	isAnimated?: boolean;
	// true = Rovo icon, ReactNode = custom icon (e.g. Agent avatar), false = no icon.
	// Has no effect on the morphing icon when animation is enabled — use disableMorphingAnimation for that.
	icon?: boolean | ReactNode;
	// whether to apply shimmer and loading icon animation effect to the title text
	animation?: boolean;
	// when true, suppresses the morphing animation icon and falls back to the icon prop
	disableMorphingAnimation?: boolean;
	// whether to show the ellipsis dots (default: true)
	showEllipsis?: boolean;
	testId?: string;
	// optional content rendered below the text (but not the icon), e.g. a progress bar
	bottomAdornment?: ReactNode;
};

const getLoadingMorphingIcon = () => {
	return <MorphingShapeLoader testId="morphing-loader" />;
};

const getIcon = (icon: ReactNode | boolean): ReactNode => {
	if (React.isValidElement(icon)) {
		return icon;
	} else if (icon) {
		return (
			<LazyAnimatedRovoIcon alt="Rovo" testId="rovo-loading-animation" width="24px" height="24px" />
		);
	}

	return null;
};

export const RovoThinkingIndicator = ({
	title,
	isAnimated = true,
	icon = true,
	animation = false,
	disableMorphingAnimation = false,
	showEllipsis = true,
	testId,
	bottomAdornment,
}: RovoThinkingIndicatorProps): JSX.Element => {
	const enableAnimation = animation;

	const iconNode =
		enableAnimation && !disableMorphingAnimation ? getLoadingMorphingIcon() : getIcon(icon);
	const textNode = (
		<Box xcss={showEllipsis ? styles.textNoWrap : styles.text}>
			{enableAnimation ? <ShimmerText>{title}</ShimmerText> : title}
		</Box>
	);
	const ellipsesNode = showEllipsis ? (
		<Box xcss={styles.ellipsesContainer}>
			<EllipsesAnimation isAnimated={isAnimated} />
		</Box>
	) : null;

	if (bottomAdornment) {
		return (
			<Box xcss={styles.rootWithAdornment} testId={testId}>
				<Box xcss={styles.iconContainer}>{iconNode}</Box>
				<Box xcss={styles.textColumn}>
					<Box xcss={styles.textRow}>
						{textNode}
						{ellipsesNode}
					</Box>
					{bottomAdornment}
				</Box>
			</Box>
		);
	}

	return (
		<Box xcss={styles.root} testId={testId}>
			<Box xcss={styles.iconContainer}>
				{iconNode}
				{textNode}
			</Box>
			{ellipsesNode}
		</Box>
	);
};
